import React, { useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Icon from './Icon'
import AiChatView from './AiChatView'
import { buildAiPageContext, formatContextForPrompt } from '../utils/aiPageContext'
import { useAuth } from '../contexts/AuthContext'
import { useAiChatRouteSummary } from '../contexts/AiChatRouteContext'
import { useMediaQuery } from '../hooks/useMediaQuery'

const MOBILE_MQ = '(max-width: 768px)'

/**
 * Мобилка: FAB → полноэкранный /ai.
 * Десктоп: колонка справа в потоке или FAB при свёрнутой панели.
 */
export default function AiChatWidget({ aiPanelOpen, onAiPanelOpenChange }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isMobile = useMediaQuery(MOBILE_MQ)
  const { humanSummary } = useAiChatRouteSummary()

  const ctx = useMemo(() => buildAiPageContext(location.pathname), [location.pathname])
  const promptHint = useMemo(
    () => formatContextForPrompt(ctx, { summaryOverride: humanSummary }),
    [ctx, humanSummary],
  )
  const summary = humanSummary ?? ctx.summary

  const hideOnRoute = location.pathname === '/login' || location.pathname.startsWith('/ai')

  const navigateToAi = useCallback(() => {
    navigate('/ai', {
      state: {
        aiContextPathname: location.pathname,
        aiContextSummary: summary,
        aiContextPromptHint: promptHint,
      },
    })
  }, [location.pathname, navigate, promptHint, summary])

  const openFullPage = useCallback(() => {
    onAiPanelOpenChange(false)
    navigateToAi()
  }, [navigateToAi, onAiPanelOpenChange])

  if (hideOnRoute) {
    return null
  }

  if (isMobile) {
    return (
      <button
        type="button"
        className="ai-chat-fab"
        aria-label="Открыть ИИ-ассистента"
        onClick={navigateToAi}
      >
        <Icon name="chat" size={24} />
      </button>
    )
  }

  return (
    <>
      {aiPanelOpen && (
        <aside className="ai-chat-column" aria-label="ИИ-ассистент">
          <AiChatView
            title="ИИ-ассистент"
            summary={summary}
            promptHint={promptHint}
            onOpenFullPage={openFullPage}
            onClosePanel={() => onAiPanelOpenChange(false)}
            user={user}
          />
        </aside>
      )}
      {!aiPanelOpen && (
        <button
          type="button"
          className="ai-chat-fab"
          aria-label="Открыть ИИ-ассистента"
          aria-expanded={false}
          onClick={() => onAiPanelOpenChange(true)}
        >
          <Icon name="chat" size={24} />
        </button>
      )}
    </>
  )
}
